import axios, { AxiosError } from "axios";
import type { AxiosRequestConfig } from "axios";
import { BASE_URL, TENANT_SLUG } from "@/config";
import { applyAxiosInterceptor } from "@/lib/axios-interceptor";
import { getTokenFromCookie } from "@/lib/cookie.ts";

export const AXIOS_INSTANCE = axios.create({ baseURL: BASE_URL });
applyAxiosInterceptor(AXIOS_INSTANCE);

// add a second `options` argument here if you want to pass extra options to each generated query
export const customInstance = <T>(
  url: string,
  config: AxiosRequestConfig & {
    body?: unknown;
    skipAuth?: boolean;
    // Opt out of the global axios interceptor's generic error toast (src/lib/axios-interceptor.js)
    // for callers that render their own error UI based on the response body.
    suppressErrorNotification?: boolean;
  },
): Promise<T> => {
  const source = axios.CancelToken.source();

  const headers = { ...(config.headers || {}) };

  const token = getTokenFromCookie();

  if (token && !config.skipAuth) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  headers["X-Tenant-Slug"] = TENANT_SLUG;
  headers["X-Fe-Version"] = import.meta.env.VITE_APP_VERSION ?? "unknown";

  const promise = AXIOS_INSTANCE({
    ...config,
    headers,
    url,
    data: config.body, // do we have some old axios version ?
    cancelToken: source.token,
  }); //.then(({ data }) => data);

  // @ts-ignore
  promise.cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise as Promise<T>;
};

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<T> = T;

// export type BodyType<T> = T extends { body: infer B } ? B : T;

// Or, in case you want to wrap the body type (optional)
// (if the custom instance is processing data before sending it, like changing the case for example)
// export type BodyType<BodyData> = CamelCase<BodyData>;
